import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  CalendarClock,
  Loader2,
  Pencil,
  Trash2,
  BarChart2,
} from 'lucide-react';
import api from '../../utils/axiosConfig';
import { fetchStravaActivityWithStreams } from '../../utils/stravaActivityApi';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import ActivityMap from '../ActivityMap';
import RecordingModeBadge from '../training/RecordingModeBadge';
import RunningWorkoutViz from '../training/RunningWorkoutViz';
import { LINKED_STRAVA_ACTIVITY_META_KEY } from './plannerUtils';

const INTERNAL_META_KEYS = new Set([
  LINKED_STRAVA_ACTIVITY_META_KEY,
  'from_strava_activity_link',
  'planned_sport',
]);

/**
 * Single card when a planned running session is linked to a Strava activity log item on the same day.
 */
export default function LinkedPlannedActivityCard({
  plannedItem,
  activityItem,
  t,
  locale,
  onOpenActivity,
  onOpenRunningDetail,
  onOpenRunningEditor,
  onItemMutated,
}) {
  const activityId = activityItem?.strava_activity_id;
  const [mapPayload, setMapPayload] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const ure = plannedItem.user_running_exercise;
  const ts = plannedItem.training_session;
  const hasUre = !!(plannedItem.user_running_exercise_id && ure);
  const ureSplits = hasUre && Array.isArray(ure.preview_splits) ? ure.preview_splits : [];
  const libSplits = ts?.preview_splits;
  const splits = hasUre ? (ureSplits.length > 0 ? ureSplits : libSplits || []) : libSplits || [];

  const cardTitle = activityItem?.title || plannedItem.title || ure?.title || ts?.name;
  const cardDescription = hasUre
    ? ure.description || plannedItem.summary || ts?.description
    : plannedItem.summary || ure?.description || ts?.description;

  const canDetail = !!(plannedItem.user_running_exercise_id || plannedItem.training_session_id);
  const canEdit = !!plannedItem.user_running_exercise_id;

  useEffect(() => {
    if (!activityId) return;
    let cancelled = false;
    setMapLoading(true);
    (async () => {
      try {
        const data = await fetchStravaActivityWithStreams(activityId);
        if (!cancelled) setMapPayload(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setMapPayload(null);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const handleDelete = async () => {
    if (!onItemMutated) return;
    setDeleting(true);
    try {
      await api.delete(`/api/training-log/items/${plannedItem.id}`);
      setConfirmDelete(false);
      await onItemMutated();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  const activity = mapPayload?.activity;
  const streams = mapPayload?.streams || {};
  const openActivity = () => {
    if (activityId) onOpenActivity?.(activityId);
  };

  const meta = plannedItem.metadata;

  return (
    <div className="rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center gap-2 border-b border-border/60 px-3 pt-3 text-muted-foreground">
        <CalendarClock className="h-4 w-4 shrink-0" />
        <p className="text-xs font-medium">{t('trainingLog.typePlannedSession')}</p>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {canDetail && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      onOpenRunningDetail?.({
                        mode: plannedItem.user_running_exercise_id ? 'instance' : 'library',
                        instanceId: plannedItem.user_running_exercise_id,
                        librarySessionId: plannedItem.training_session_id,
                        title: cardTitle,
                      })
                    }
                    aria-label={t('runningExercise.openDetail')}
                  >
                    <BarChart2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('runningExercise.openDetail')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {canEdit && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => onOpenRunningEditor?.(plannedItem.user_running_exercise_id, plannedItem)}
                    aria-label={t('runningExercise.editPlan')}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('runningExercise.editPlan')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {!!onItemMutated && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              aria-label={t('trainingLog.removePlannedSession')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label={t('trainingLog.viewActivity')}
        className={cn(
          'cursor-pointer p-3 pt-2 outline-none transition-colors hover:bg-muted/25',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background'
        )}
        onClick={openActivity}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openActivity();
          }
        }}
      >
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('trainingLog.typeActivity')}
        </p>
        {!!cardTitle && <p className="mt-0.5 text-sm font-semibold text-foreground">{cardTitle}</p>}
        {!!activityItem?.summary && (
          <p className="mt-1 text-xs text-muted-foreground">{activityItem.summary}</p>
        )}
        {!!cardDescription && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{cardDescription}</p>
        )}
        {splits.length > 0 && (
          <div className="mt-3">
            <RunningWorkoutViz splits={splits} heightPx={36} />
          </div>
        )}
        <div className="mt-3">
          {mapLoading && (
            <div className="flex h-36 items-center justify-center rounded-md border border-border/60 bg-muted/20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!mapLoading && (activity?.polyline || activity?.summary_polyline) && (
            <div className="overflow-hidden rounded-md border border-border/60">
              <ActivityMap
                encodedPolyline={activity.polyline}
                summaryPolyline={activity.summary_polyline}
                streams={streams}
                height={200}
              />
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border/40 px-3 pb-3 pt-2">
        <p className="text-[10px] text-muted-foreground">
          {format(new Date(plannedItem.occurred_at), 'PPp', { locale })}
        </p>
        {!!confirmDelete && (
          <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <p className="text-xs font-medium text-foreground">{t('trainingLog.deletePlannedConfirmTitle')}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{t('trainingLog.deletePlannedConfirmHint')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" disabled={deleting} onClick={() => setConfirmDelete(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="button" size="sm" variant="destructive" disabled={deleting} onClick={handleDelete}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trainingLog.removePlannedSession')}
              </Button>
            </div>
          </div>
        )}
        {((meta && typeof meta === 'object' && Object.keys(meta).length > 0) || (ure?.recording_mode && ure.recording_mode !== 'outdoor')) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {meta && typeof meta === 'object' && Object.entries(meta)
              .filter(([k]) => !INTERNAL_META_KEYS.has(k))
              .map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px] font-normal">
                  {k}: {String(v)}
                </Badge>
              ))}
            {meta?.planned_sport && (
              <Badge variant="secondary" className="text-[10px] font-normal">
                {(() => {
                  const sk = `trainingLog.plannedSport.${meta.planned_sport}`;
                  const lbl = t(sk);
                  return lbl === sk ? String(meta.planned_sport) : lbl;
                })()}
              </Badge>
            )}
            <RecordingModeBadge mode={ure?.recording_mode} />
          </div>
        )}
      </div>
    </div>
  );
}
